import React, { useRef, useEffect, useState } from "react";
import "./InfoBox.css";

const InfoBox = ({ dealer, onClose, pinPosition }) => {
  const tooltipRef = useRef(null);
  const [tooltipDimensions, setTooltipDimensions] = useState({
    width: 500,
    height: 300,
  });
  const [isPositioned, setIsPositioned] = useState(false);

  // Measure tooltip dimensions after first render
  useEffect(() => {
    if (tooltipRef.current && !isPositioned) {
      const rect = tooltipRef.current.getBoundingClientRect();
      console.log(
        `Measured tooltip dimensions: width=${rect.width}, height=${rect.height}`
      );
      setTooltipDimensions({ width: rect.width, height: rect.height });
      setIsPositioned(true);
    }
  }, [isPositioned, dealer]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Generate ICS file for a specific date
  const generateICSForDate = (dateEntry) => {
    const startDate = new Date(dateEntry.date);

    // Format dates for ICS (YYYYMMDDTHHMMSSZ)
    const formatICSDate = (date, time) => {
      const [hours, minutes] = time.split(":");
      const icsDate = new Date(date);
      icsDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      return icsDate.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    };

    const startDateTime = formatICSDate(startDate, dateEntry.startTime);
    const endDateTime = formatICSDate(startDate, dateEntry.endTime);

    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Carado//Showcar Tour//DE",
      "BEGIN:VEVENT",
      `UID:carado-tour-${dealer.id}-${dateEntry.date}@carado.com`,
      `DTSTART:${startDateTime}`,
      `DTEND:${endDateTime}`,
      `SUMMARY:Carado Showcar Tour - ${dealer.dealerName}`,
      `DESCRIPTION:Besuchen Sie uns bei ${
        dealer.dealerName
      } zur Carado Showcar Tour. ${
        dealer.url ? `Weitere Informationen: ${dealer.url}` : ""
      }`,
      `LOCATION:${dealer.address}`,
      "STATUS:CONFIRMED",
      "BEGIN:VALARM",
      "TRIGGER:-P1D",
      "ACTION:DISPLAY",
      "DESCRIPTION:Carado Showcar Tour Erinnerung",
      "END:VALARM",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");

    return icsContent;
  };

  // Download ICS file for specific date
  const downloadCalendarForDate = (dateEntry, event) => {
    console.log("Calendar download clicked for:", dateEntry.displayDate);

    try {
      const icsContent = generateICSForDate(dateEntry);
      const blob = new Blob([icsContent], {
        type: "text/calendar;charset=utf-8",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `carado-tour-${dealer.dealerName.replace(
        /[^a-zA-Z0-9]/g,
        "-"
      )}-${dateEntry.displayDate.replace(/\./g, "-")}.ics`;
      document.body.appendChild(link);
      link.click();
      // Small delay before removing the link to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        console.log("Calendar file downloaded successfully");
      }, 100);
    } catch (error) {
      console.error("Error downloading calendar:", error);
    }
  };

  // Open dealer website
  const visitWebsite = (event) => {
    console.log("Website link clicked for:", dealer.dealerName);

    if (dealer.url) {
      let url = dealer.url;
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        url = "https://" + url;
      }
      console.log("Opening website:", url);
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // Open route planner
  const openRoutePlanner = (event) => {
    console.log("Route planner clicked for:", dealer.address);

    const destination = encodeURIComponent(dealer.address);
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    console.log("Opening route planner:", mapsUrl);
    window.open(mapsUrl, "_blank", "noopener,noreferrer");
  };

  // Calculate tooltip position based on pin position and available space
  const calculatePosition = () => {
    if (!pinPosition) return { top: "50%", left: "50%" };

    const tooltipWidth = tooltipDimensions.width;
    const tooltipHeight = tooltipDimensions.height;
    const pinHeight = 80; // Approximate pin height
    const minSpace = 40; // Minimum space needed to avoid overlap

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let position = {};
    let placement = "below";

    // Calculate available space without overlapping the pin
    const spaceAbove = pinPosition.y - 80; // Space above pin (top of screen to pin top)
    const spaceBelow = viewportHeight - (pinPosition.y + pinHeight + minSpace); // Space below pin (pin bottom to screen bottom)

    console.log(
      `Tooltip positioning: Pin at y=${pinPosition.y}, spaceAbove=${spaceAbove}, spaceBelow=${spaceBelow}, tooltipHeight=${tooltipHeight}`
    );

    // Try to place tooltip without covering the pin
    if (spaceBelow >= tooltipHeight) {
      // Place below pin with smaller offset
      placement = "below";
      position.top = pinPosition.y + pinHeight / 3;
      console.log(`Placing below pin: top=${position.top}`);
    } else if (spaceAbove >= tooltipHeight) {
      // Place above pin with larger offset
      placement = "above";
      position.bottom = viewportHeight - pinPosition.y + pinHeight * 1.25;
      console.log(`Placing above pin: bottom=${position.bottom}`);
    } else {
      // Not enough space on either side without covering pin
      // Choose the side with more space and allow some overlap
      if (spaceAbove > spaceBelow) {
        placement = "above";
        // Ensure tooltip doesn't go off-screen at the top
        const maxBottom = viewportHeight - 20;
        position.bottom = Math.min(
          maxBottom,
          viewportHeight - pinPosition.y + 10
        );
        console.log(`Overlapping above pin: bottom=${position.bottom}`);
      } else {
        placement = "below";
        // Ensure tooltip doesn't go off-screen at the bottom
        const maxTop = viewportHeight - tooltipHeight - 20;
        position.top = Math.min(maxTop, pinPosition.y + pinHeight - 10);
        console.log(`Overlapping below pin: top=${position.top}`);
      }
    }

    // Horizontal positioning
    let left = window.innerWidth / 2 - tooltipWidth / 2;

    // Keep within viewport bounds
    if (left < 20) {
      left = 20;
    } else if (left + tooltipWidth > viewportWidth - 20) {
      left = viewportWidth - tooltipWidth - 20;
    }

    position.left = left;

    return { position, placement };
  };

  const { position, placement } = calculatePosition();

  return (
    <div
      ref={tooltipRef}
      className={`info-box-tooltip ${placement}`}
      style={{
        position: "fixed",
        ...(isPositioned
          ? position
          : {
              // Initial position for measurement (off-screen)
              top: -9999,
              left: -9999,
              visibility: isPositioned ? "visible" : "hidden",
            }),
        zIndex: 1000,
      }}
      onClick={(e) => {
        console.log("Tooltip content clicked");
        e.stopPropagation();
      }}
    >
      <div className="info-box">
        {/* Close button */}
        <button
          className="info-box-close"
          onClick={(e) => {
            console.log("Close button clicked");
            e.stopPropagation();
            onClose();
          }}
          aria-label="Schließen"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="10" fill="#08364b" />
            <path
              d="M13 7L7 13M7 7l6 6"
              stroke="white"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Header with location icon and text */}
        <div className="info-box-header">
          <div className="header-content">
            <div className="header-text">
              <div className="event-label">Zukunftstour</div>
              <div className="dealer-name">{dealer.dealerName}</div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="info-box-content">
          {/* Location info */}
          <div className="location-section">
            <div className="icon-row">
              <div className="header-icon">
                <img
                  src="/dealer_icon.svg"
                  className="location-icon"
                  alt="Location"
                />
              </div>
              <div>
                <div className="location-text">{dealer.eventType}</div>
              </div>
            </div>
            <div className="icon-row">
              <div className="header-icon">
                <img
                  src="/map_icon.svg"
                  className="location-icon"
                  alt="Location"
                />
              </div>
              <div>
                <div className="location-text">
                  {dealer.location || dealer.address}
                </div>
                <div className="location-subtext">
                  <button
                    type="button"
                    className="location-link"
                    onClick={openRoutePlanner}
                  >
                    zum Routenplaner
                  </button>
                </div>
              </div>
            </div>
            {dealer.url && (
              <div className="icon-row">
                <div className="header-icon">
                  <img
                    src="/link_icon.svg"
                    className="location-icon"
                    alt="Location"
                  />
                </div>

                <button
                  type="button"
                  className="website-link"
                  onClick={visitWebsite}
                >
                  {dealer.url.replace(/^https?:\/\//, "")}
                </button>
              </div>
            )}
          </div>

          {/* Dates list */}
          <div className="dates-section">
            {dealer.dates &&
              dealer.dates.map((dateEntry, index) => (
                <div key={index} className="date-entry">
                  <div className="time-display">
                    {dateEntry.startTime} - {dateEntry.endTime} Uhr
                  </div>
                  <div className="date-display">{dateEntry.displayDate}</div>
                  <button
                    className="termin-link"
                    onClick={(event) =>
                      downloadCalendarForDate(dateEntry, event)
                    }
                  >
                    Termin speichern
                  </button>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoBox;
